import XLSX from 'xlsx-js-style';

export function generateBoxesRutaXLSX(boxes, manifestId, boxRecipients = []) {
  // Route order must mirror GuiasPortal.js sortedStops: display_sequence
  // ascending, unsequenced boxes last, created_at as the stable tiebreak.
  const filteredBoxes = boxes
    .filter(b => b.manifest_id === manifestId)
    .sort((a, b) => {
      const seqA = a.display_sequence ?? Infinity;
      const seqB = b.display_sequence ?? Infinity;
      if (seqA !== seqB) return seqA - seqB;
      const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tA - tB;
    });
  const today = new Date().toISOString().slice(0, 10);
  const cols = 'ABCDEFGHIJKLMNO';

  // ── Sheet 1: "Ruta de entrega" ─────────────────────────────────────────────
  const ws1 = {};
  const merges1 = [];

  // Row 1: merged section headers
  ws1['A1'] = { v: 'Ruta de entrega', t: 's' };
  ws1['G1'] = { v: 'Datos para Guías SV', t: 's' };
  merges1.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }); // A1:F1

  // Row 2: column headers
  const row2Headers = [
    'Nombre/Codigo', 'Caja No', 'Dimensiones', 'Weight', '', 'Categoría',
    'Caja No.', 'Destinatario', 'Teléfono', 'Dirección', 'Municipio', 'Departamento', 'País', 'Peso', 'Observaciones',
  ];
  row2Headers.forEach((h, i) => { ws1[`${cols[i]}2`] = { v: h, t: 's' }; });

  // Row 3: weight sub-headers
  ws1['D3'] = { v: 'Pounds', t: 's' };
  ws1['E3'] = { v: 'KG', t: 's' };

  // Data rows: start at row 4 (1-indexed)
  filteredBoxes.forEach((box, idx) => {
    const r = idx + 4;
    const n = idx + 1;
    const wLb = parseFloat(box.weight_lb) || null;

    ws1[`A${r}`] = { v: box.customer_id || '',        t: 's' };
    ws1[`B${r}`] = { v: n,                            t: 'n' };
    ws1[`C${r}`] = { v: box.dimensions || '',         t: 's' };
    ws1[`D${r}`] = wLb != null ? { v: wLb, t: 'n' } : { v: '', t: 's' };
    ws1[`E${r}`] = { f: `D${r}/2.2046`,               t: 'n' };
    ws1[`F${r}`] = { v: 'Casillero',                  t: 's' };
    ws1[`G${r}`] = { v: n,                            t: 'n' };
    ws1[`H${r}`] = { v: box.recipient_name || '',     t: 's' };
    ws1[`I${r}`] = { v: box.recipient_phone || '',    t: 's' };
    ws1[`J${r}`] = { v: box.recipient_address || '',  t: 's' };
    ws1[`K${r}`] = { v: box.municipio || '',          t: 's' };
    ws1[`L${r}`] = { v: box.departamento || '',       t: 's' };
    ws1[`M${r}`] = { v: 'El Salvador',                t: 's' };
    ws1[`N${r}`] = wLb != null ? { v: wLb, t: 'n' } : { v: '', t: 's' };
    ws1[`O${r}`] = { v: box.internal_comments || '',  t: 's' };
  });

  const lastRow1 = Math.max(3, filteredBoxes.length + 3);
  ws1['!ref']    = `A1:O${lastRow1}`;
  ws1['!merges'] = merges1;
  ws1['!cols']   = [
    { wch: 16 }, { wch: 8  }, { wch: 16 }, { wch: 10 }, { wch: 10 },
    { wch: 12 }, { wch: 8  }, { wch: 20 }, { wch: 14 }, { wch: 20 },
    { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 20 },
  ];

  // ── Sheet 2: "Guia" ────────────────────────────────────────────────────────
  const ws2 = {};
  const guiaCols = 'ABCDEFGHI';
  const guiaHeaders = [
    'Orden', 'Destinatario', 'Teléfono', 'Dirección',
    'Municipio', 'Departamento', 'País', 'Peso', 'Observaciones',
  ];
  guiaHeaders.forEach((h, i) => { ws2[`${guiaCols[i]}1`] = { v: h, t: 's' }; });

  // Build Guia rows — one row per recipient
  // Priority: box_recipients rows first; fall back to inline box fields if no box_recipients exist for that box
  const guiaRows = [];
  let guiaOrder = 1;
  filteredBoxes.forEach((box) => {
    const recipients = boxRecipients.filter(r => r.box_id === box.box_id);
    if (recipients.length > 0) {
      recipients.forEach(r => {
        guiaRows.push([
          guiaOrder++,
          r.recipient_name || '',
          r.recipient_phone || '',
          r.recipient_address || '',
          box.municipio || '',
          box.departamento || '',
          'El Salvador',
          box.weight_lb || '',
          box.internal_comments || '',
        ]);
      });
    } else {
      guiaRows.push([
        guiaOrder++,
        box.recipient_name || '',
        box.recipient_phone || '',
        box.recipient_address || '',
        box.municipio || '',
        box.departamento || '',
        'El Salvador',
        box.weight_lb || '',
        box.internal_comments || '',
      ]);
    }
  });

  guiaRows.forEach((rowData, idx) => {
    const r = idx + 2;
    rowData.forEach((val, ci) => {
      ws2[`${guiaCols[ci]}${r}`] = typeof val === 'number'
        ? { v: val, t: 'n' }
        : { v: String(val), t: 's' };
    });
  });

  const lastRow2 = Math.max(1, guiaRows.length + 1);
  ws2['!ref']  = `A1:I${lastRow2}`;
  ws2['!cols'] = [
    { wch: 8  }, { wch: 20 }, { wch: 14 }, { wch: 20 },
    { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 20 },
  ];

  // ── Build workbook and trigger download ────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, 'Ruta de entrega');
  XLSX.utils.book_append_sheet(wb, ws2, 'Guia');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Ruta-${manifestId}-${today}.xlsx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}
